/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useHasExternalIntegration } from "@/api/hooks/useIntegrations";

interface RequireIntegrationProps {
  children: React.ReactNode;
}

/**
 * Route guard that redirects to the dashboard when no external
 * integration (Jira, ServiceNow, Azure DevOps) is configured.
 * While the integration check is loading, shows a spinner to
 * avoid a flash of the wrong page.
 */
export function RequireIntegration({ children }: RequireIntegrationProps) {
  const { hasIntegration, isLoading } = useHasExternalIntegration();

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center" role="status">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <span className="sr-only">Loading…</span>
      </div>
    );
  }

  if (!hasIntegration) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
